import React, { useMemo } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar, Clock, AlertCircle, User } from 'lucide-react';
import { Task, TaskPriority } from '@/types';
import { format, differenceInDays, isAfter, isBefore } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const GanttChart: React.FC = () => {
  const { currentProject, getProjectTasks } = useProject();
  const tasks = currentProject ? getProjectTasks(currentProject.id) : [];

  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  const getUserById = (userId: string) => mockUsers.find(u => u.id === userId);

  const { timelineData, dateRange } = useMemo(() => {
    if (!tasks.length) return { timelineData: [], dateRange: { start: new Date(), end: new Date() } };

    // Calculate date range
    const startDates = tasks.map(task => new Date(task.startDate));
    const endDates = tasks.map(task => new Date(task.endDate));
    const minDate = new Date(Math.min(...startDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...endDates.map(d => d.getTime())));

    // Add some padding to the range
    const rangeStart = new Date(minDate);
    rangeStart.setDate(rangeStart.getDate() - 3);
    const rangeEnd = new Date(maxDate);
    rangeEnd.setDate(rangeEnd.getDate() + 3);

    const totalDays = differenceInDays(rangeEnd, rangeStart);

    // Process tasks for timeline
    const processedTasks = tasks.map(task => {
      const taskStart = new Date(task.startDate);
      const taskEnd = new Date(task.endDate);
      const taskDuration = differenceInDays(taskEnd, taskStart) + 1;
      const startOffset = differenceInDays(taskStart, rangeStart);
      const widthPercentage = (taskDuration / totalDays) * 100;
      const leftPercentage = (startOffset / totalDays) * 100;

      return {
        ...task,
        widthPercentage,
        leftPercentage,
        duration: taskDuration
      };
    });

    // Sort by start date
    processedTasks.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

    return {
      timelineData: processedTasks,
      dateRange: { start: rangeStart, end: rangeEnd }
    };
  }, [tasks]);

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT: return 'bg-red-500';
      case TaskPriority.HIGH: return 'bg-orange-500';
      case TaskPriority.MEDIUM: return 'bg-yellow-500';
      case TaskPriority.LOW: return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-600';
      case 'in_progress': return 'bg-blue-600';
      case 'review': return 'bg-yellow-600';
      default: return 'bg-gray-600';
    }
  };

  const generateTimeline = () => {
    const weeks = [];
    const totalDays = differenceInDays(dateRange.end, dateRange.start);
    const weeksCount = Math.ceil(totalDays / 7);

    for (let i = 0; i < weeksCount; i++) {
      const weekStart = new Date(dateRange.start);
      weekStart.setDate(weekStart.getDate() + i * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      weeks.push({
        start: weekStart,
        end: weekEnd,
        leftPercentage: (i * 7 / totalDays) * 100,
        widthPercentage: (7 / totalDays) * 100
      });
    }

    return weeks;
  };

  const timelineWeeks = generateTimeline();

  if (!currentProject) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum projeto selecionado</h3>
          <p className="text-gray-600">Selecione um projeto na lista de projetos para visualizar o gráfico de Gantt</p>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma tarefa ainda</h3>
          <p className="text-gray-600">Adicione tarefas ao projeto para visualizar o cronograma</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gráfico de Gantt</h1>
          <p className="text-gray-600">{currentProject.name}</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">
              {format(dateRange.start, 'dd MMM', { locale: ptBR })} - {format(dateRange.end, 'dd MMM yyyy', { locale: ptBR })}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">{tasks.length} tarefas</span>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Cronograma do Projeto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Timeline Header */}
            <div className="relative">
              <div className="flex border-b border-gray-200 pb-2">
                <div className="w-80 flex-shrink-0 font-medium text-gray-700">
                  Tarefa
                </div>
                <div className="flex-1 relative">
                  <div className="flex">
                    {timelineWeeks.map((week, index) => (
                      <div
                        key={index}
                        className="border-r border-gray-200 text-center py-2 text-xs font-medium text-gray-600"
                        style={{
                          width: `${week.widthPercentage}%`,
                          minWidth: '60px'
                        }}
                      >
                        {format(week.start, 'dd/MM', { locale: ptBR })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Tasks */}
            <div className="space-y-3">
              {timelineData.map((task, index) => {
                const assignedUser = task.assignedTo ? getUserById(task.assignedTo) : null;
                const isOverdue = new Date(task.endDate) < new Date() && task.status !== 'done';

                return (
                  <div key={task.id} className="flex items-center">
                    {/* Task Info */}
                    <div className="w-80 flex-shrink-0 pr-4">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${getPriorityColor(task.priority)}`} />
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{task.title}</h4>
                          <div className="flex items-center space-x-2 mt-1">
                            {assignedUser ? (
                              <Avatar className="w-4 h-4">
                                <AvatarImage src={assignedUser.avatar} alt={assignedUser.name} />
                                <AvatarFallback className="text-xs">
                                  {assignedUser.name.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                              </Avatar>
                            ) : (
                              <User className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="text-xs text-gray-500">
                              {assignedUser ? assignedUser.name : 'Não atribuído'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Timeline Bar */}
                    <div className="flex-1 relative h-10 bg-gray-50 rounded">
                      <div
                        className={`absolute top-1 h-8 rounded ${getStatusColor(task.status)} opacity-80 flex items-center justify-center`}
                        style={{
                          left: `${task.leftPercentage}%`,
                          width: `${task.widthPercentage}%`,
                          minWidth: '20px'
                        }}
                      >
                        <div className="text-white text-xs font-medium px-2 truncate">
                          {task.duration}d
                        </div>
                      </div>
                      
                      {/* Progress overlay */}
                      {task.progress > 0 && (
                        <div
                          className="absolute top-1 h-8 bg-green-600 rounded opacity-60"
                          style={{
                            left: `${task.leftPercentage}%`,
                            width: `${(task.widthPercentage * task.progress) / 100}%`
                          }}
                        />
                      )}
                    </div>

                    {/* Task Details */}
                    <div className="w-40 flex-shrink-0 pl-4">
                      <div className="text-xs text-gray-600 space-y-1">
                        <div className="flex items-center justify-between">
                          <span>Início:</span>
                          <span>{format(new Date(task.startDate), 'dd/MM', { locale: ptBR })}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Fim:</span>
                          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            {format(new Date(task.endDate), 'dd/MM', { locale: ptBR })}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Progresso:</span>
                          <span>{task.progress}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle>Legenda</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Prioridade</h4>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-xs text-gray-600">Urgente</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  <span className="text-xs text-gray-600">Alta</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="text-xs text-gray-600">Média</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-xs text-gray-600">Baixa</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Status</h4>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-gray-600" />
                  <span className="text-xs text-gray-600">A Fazer</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-blue-600" />
                  <span className="text-xs text-gray-600">Em Andamento</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-yellow-600" />
                  <span className="text-xs text-gray-600">Em Revisão</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded bg-green-600" />
                  <span className="text-xs text-gray-600">Concluído</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};